// Persistent skip-trace run panel. One per submitted batch; several can
// stack. Appears immediately on submit (optimistic "queued"), then
// self-polls the agent status endpoint every 15s while running, rendering
// the live stage headline (e.g. "fetch — 9/16 docs") + detail + elapsed.
// Freezes on a terminal state showing final counts / cost / Close
// smart-view link (done) or the reason (failed / blocked). Never
// auto-clears — only the × dismisses it (the parent then drops it from
// state + localStorage).

import { useEffect, useState } from 'react';
import {
  agentApi, readStatusView, readProgress, progressHeadline, readFinal,
  readWeldSession, isSessionBlocked, isTerminalStatus, isFailedStatus,
  readBlockReason, formatAge,
  type SkipTraceStatusView, type ProgressView, type FinalView, type WeldSession,
} from '../services/agentApi';

interface Props {
  runId: string;
  label: string;
  startedAt: number;   // epoch ms
  queued: number;
  onDismiss: (runId: string) => void;
}

function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), rs = s % 60;
  if (m < 60) return `${m}m ${String(rs).padStart(2, '0')}s`;
  const h = Math.floor(m / 60), rm = m % 60;
  return `${h}h ${String(rm).padStart(2, '0')}m`;
}

export function SkipTraceRunPanel({ runId, label, startedAt, queued, onDismiss }: Props) {
  const [status, setStatus] = useState<SkipTraceStatusView | null>(null);
  const [progress, setProgress] = useState<ProgressView | null>(null);
  const [final, setFinal] = useState<FinalView | null>(null);
  const [weldSession, setWeldSession] = useState<WeldSession | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [terminal, setTerminal] = useState(false);
  const [failed, setFailed] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Poll status every 15s while not terminal; immediate poll on mount so a
  // reload-restored panel fills in without waiting a full interval.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const raw = await agentApi.skipTraceStatus(runId);
        if (cancelled) return;
        const view = readStatusView(raw);
        setStatus(view);
        setProgress(readProgress(raw));
        const ws = readWeldSession(raw);
        if (ws) setWeldSession(ws);
        if (isSessionBlocked(raw)) { setBlocked(true); setReason(readBlockReason(raw)); }
        else setBlocked(false);
        if (isTerminalStatus(view)) {
          setTerminal(true);
          setFinal(readFinal(raw));
          if (isFailedStatus(view)) { setFailed(true); setReason(readBlockReason(raw) ?? view.status ?? 'failed'); }
          clearInterval(timer);
        }
      } catch { /* transient — keep polling */ }
    };
    poll();
    const timer = setInterval(poll, 15_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [runId]);

  // Live elapsed while running; stops (freezes) once terminal.
  useEffect(() => {
    if (terminal) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [terminal]);

  const elapsed = formatElapsed(now - startedAt);
  const sessionAge = weldSession?.age ?? (weldSession?.ageSeconds != null ? formatAge(weldSession.ageSeconds) : null);
  const headline = progressHeadline(progress)
    ?? (status ? 'working…' : `queued ${queued}`);   // optimistic before first poll

  const accent = failed ? 'red' : blocked ? 'amber' : terminal ? 'green' : 'blue';
  const borderCls = { red: 'border-red/50', amber: 'border-amber/50', green: 'border-green/40', blue: 'border-blue/50' }[accent];
  const bgCls = { red: 'bg-red/10', amber: 'bg-amber/10', green: 'bg-green/5', blue: 'bg-blue/10' }[accent];

  return (
    <div className={`rounded border ${borderCls} ${bgCls} px-4 py-3`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex items-center gap-2 flex-wrap">
          <span className="text-[13px] text-text font-medium truncate">{label}</span>
          <span className="text-[10px] font-mono text-text-faint">run {runId}</span>
          <span className="text-[11px] font-mono text-text-dim">· {elapsed}</span>
          {sessionAge && <span className="text-[10px] font-mono text-text-faint">· session {sessionAge}</span>}
        </div>
        <button onClick={() => onDismiss(runId)} title="Dismiss"
          className="text-text-faint hover:text-text text-base leading-none shrink-0">×</button>
      </div>

      {/* Blocked / failed reason, prominently. */}
      {blocked && !terminal && (
        <div className="mt-2 text-[12px] text-amber">
          Blocked{reason ? ` — ${reason}` : ' — Weld session needs refresh'}. Refresh the session and re-submit.
        </div>
      )}
      {failed && (
        <div className="mt-2 text-[14px] text-red font-medium">Failed{reason ? ` — ${reason}` : ''}</div>
      )}

      {/* Running: prominent stage headline + detail + live counts. */}
      {!terminal && !failed && (
        <div className="mt-1.5">
          <div className={'text-[15px] font-medium ' + (blocked ? 'text-amber' : 'text-blue')}>{headline}</div>
          {progress?.detail && (
            <div className="text-[12px] text-text-dim mt-0.5">{progress.detail}</div>
          )}
          {status && (
            <div className="mt-1.5 text-[11px] font-mono text-text-faint flex items-center gap-3 flex-wrap">
              <span>queued {status.queued ?? '–'}</span>
              <span className="text-amber">running {status.running ?? '–'}</span>
              <span className="text-green">done {status.done ?? '–'}</span>
              <span className="text-red">failed {status.failed ?? '–'}</span>
            </div>
          )}
        </div>
      )}

      {/* Done: final counts + cost + Close smart-view link. */}
      {terminal && !failed && (
        <div className="mt-1.5">
          <div className="text-[13px] text-green font-medium mb-1">Complete</div>
          <div className="text-[12px] font-mono text-text-dim flex items-center gap-3 flex-wrap">
            {final?.traced  != null && <span className="text-green">traced {final.traced}</span>}
            {final?.noMatch != null && <span className="text-text-dim">no-match {final.noMatch}</span>}
            {final?.estate  != null && <span className="text-amber">estate {final.estate}</span>}
            {final?.costUsd != null && <span className="text-text-faint">${final.costUsd.toFixed(2)}</span>}
            {(final == null || (final.traced == null && final.noMatch == null)) && (
              <span className="text-text-faint">{queued} owners</span>
            )}
          </div>
          {final?.smartViewUrl && (
            <a href={final.smartViewUrl} target="_blank" rel="noreferrer"
              className="mt-1.5 inline-block text-[12px] text-accent hover:underline">
              Open Close smart view ↗
            </a>
          )}
        </div>
      )}
    </div>
  );
}
